import type { Theme, SxProps } from '@mui/material/styles';
import type { ButtonBaseProps } from '@mui/material/ButtonBase';

import { useMemo, useCallback } from 'react';
import { usePopover } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import { CircularProgress } from '@mui/material';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';

import { CONFIG } from 'src/global-config';
import { useSelectedTenant } from 'src/layouts/provider';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { HorizontalStack } from 'src/components/custom';
import { CustomPopover } from 'src/components/custom-popover';

// ----------------------------------------------------------------------

type OutputTenantMembershipDto = MyApi.OutputTenantMembershipDto;

type WorkspaceItem = {
  id: string;
  name: string;
  logo: string;
  plan: string;
  status: OutputTenantMembershipDto['tenantStatus'];
};

const DEFAULT_WORKSPACE_LOGO = `${CONFIG.assetsDir}/assets/icons/workspaces/logo-1.webp`;

const TENANT_STATUS_LABEL: Record<
  NonNullable<OutputTenantMembershipDto['tenantStatus']>,
  string
> = {
  active: '运营中',
  frozen: '已冻结',
  clearing: '清算中',
  terminated: '已终止',
};

/** 将租户成员关系映射为 Popover 展示结构 */
function toWorkspaceItem(tenant: OutputTenantMembershipDto): WorkspaceItem {
  return {
    id: tenant.tenantId,
    name: tenant.tenantName,
    logo: tenant.logoUrl || DEFAULT_WORKSPACE_LOGO,
    plan: tenant.tenantTypeText ?? '',
    status: tenant.tenantStatus,
  };
}

/** 运营中租户使用 info 标签色 */
function getPlanLabelColor(status?: OutputTenantMembershipDto['tenantStatus']) {
  return status === 'active' ? 'info' : 'default';
}

// ----------------------------------------------------------------------

export type WorkspacesPopoverProps = ButtonBaseProps;

export function WorkspacesPopover({ sx, ...other }: WorkspacesPopoverProps) {
  const mediaQuery = 'sm';

  const { tenants, tenantId, membership, loading, switching, switchTenant } = useSelectedTenant();

  const { open, anchorEl, onClose, onOpen } = usePopover();

  const options = useMemo(() => tenants.map(toWorkspaceItem), [tenants]);

  const workspace = useMemo(() => {
    if (membership) return toWorkspaceItem(membership);
    return options[0];
  }, [membership, options]);

  const handleChangeWorkspace = useCallback(
    async (newValue: WorkspaceItem) => {
      if (newValue.id === tenantId || switching) {
        onClose();
        return;
      }
      try {
        await switchTenant(newValue.id);
      } finally {
        onClose();
      }
    },
    [onClose, switchTenant, tenantId, switching]
  );

  const buttonBg: SxProps<Theme> = {
    height: 1,
    zIndex: -1,
    opacity: 0,
    content: "''",
    borderRadius: 1,
    position: 'absolute',
    visibility: 'hidden',
    bgcolor: 'action.hover',
    width: 'calc(100% + 8px)',
    transition: (theme) =>
      theme.transitions.create(['opacity', 'visibility'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
      }),
    ...(open && {
      opacity: 1,
      visibility: 'visible',
    }),
  };

  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        width={160}
        height={32}
        sx={{ display: { xs: 'none', [mediaQuery]: 'block' } }}
      />
    );
  }

  if (!workspace) {
    return null;
  }

  const renderButton = () => (
    <ButtonBase
      disableRipple
      disabled={switching}
      onClick={onOpen}
      sx={[
        {
          py: 0.5,
          gap: { xs: 0.5, [mediaQuery]: 1 },
          '&::before': buttonBg,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Box
        component="img"
        alt={workspace.name}
        src={workspace.logo}
        sx={{ width: 24, height: 24, borderRadius: '50%' }}
      />

      <Box
        component="span"
        sx={{ typography: 'subtitle2', display: { xs: 'none', [mediaQuery]: 'inline-flex' } }}
      >
        {workspace.name}
      </Box>

      <Label
        color={getPlanLabelColor(workspace.status)}
        sx={{
          height: 22,
          cursor: 'inherit',
          display: { xs: 'none', [mediaQuery]: 'inline-flex' },
        }}
      >
        {workspace.plan}
      </Label>

      <Iconify width={16} icon="carbon:chevron-sort" sx={{ color: 'text.disabled' }} />
    </ButtonBase>
  );

  const renderMenuList = () => (
    <CustomPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      slotProps={{
        arrow: { placement: 'top-left' },
        paper: { sx: { mt: 0.5, ml: -1.55, width: 260 } },
      }}
    >
      <Scrollbar sx={{ maxHeight: 280 }}>
        <MenuList>
          {options.map((option) => (
            <MenuItem
              key={option.id}
              selected={option.id === tenantId}
              disabled={switching}
              onClick={() => handleChangeWorkspace(option)}
              sx={{ height: 48 }}
            >
              <Avatar alt={option.name} src={option.logo} sx={{ width: 24, height: 24 }} />
              <Typography
                noWrap
                component="span"
                variant="body2"
                sx={{ flexGrow: 1, fontWeight: 'fontWeightMedium' }}
              >
                {option.name}
              </Typography>
              <HorizontalStack spacing={0.5}>
                {switching && option.id === tenantId && (
                  <CircularProgress size={14} sx={{ color: 'primary.main' }} />
                )}
                <Label color={getPlanLabelColor(option.status)}>{option.plan}</Label>
              </HorizontalStack>
            </MenuItem>
          ))}
        </MenuList>
      </Scrollbar>
    </CustomPopover>
  );

  return (
    <>
      {renderButton()}
      {renderMenuList()}
    </>
  );
}
